/**
 * useKyc — start, poll, and read KYC status for the current user.
 *
 * Status field is the authoritative gate (set ONLY by stripe-webhook):
 *   unverified | pending | requires_input | verified | canceled | rejected
 *
 * `start()` calls the create-kyc-session edge function and returns a
 * Stripe Identity hosted URL the member completes in a new tab/window.
 * On return, we re-pull the profile row so the UI can reflect the new
 * pending/processing state immediately.
 */
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/lib/profile/useProfile";
import type { KycStatus } from "@/lib/profile/types";

export interface KycView {
  status: KycStatus;
  verifiedAt: string | null;
  provider: string | null;
  sessionId: string | null;
  lastAttemptAt: string | null;
  isVerified: boolean;
  isPending: boolean;
  needsAction: boolean;
}

export function useKyc() {
  const { profile, reload } = useProfile();

  const view: KycView = {
    status: (profile?.kyc_status ?? "unverified") as KycStatus,
    verifiedAt: profile?.kyc_verified_at ?? null,
    provider: profile?.kyc_provider ?? null,
    sessionId: (profile as unknown as { kyc_session_id?: string | null })
      ?.kyc_session_id ?? null,
    lastAttemptAt:
      (profile as unknown as { kyc_last_attempt_at?: string | null })
        ?.kyc_last_attempt_at ?? null,
    isVerified: profile?.kyc_status === "verified",
    isPending: profile?.kyc_status === "pending",
    needsAction:
      profile?.kyc_status === "requires_input" ||
      profile?.kyc_status === "canceled" ||
      profile?.kyc_status === "rejected" ||
      profile?.kyc_status === "unverified",
  };

  const start = useCallback(
    async (returnUrl: string): Promise<{ url?: string; error?: string }> => {
      const { data, error } = await supabase.functions.invoke(
        "create-kyc-session",
        { body: { return_url: returnUrl } },
      );
      if (error) return { error: error.message };
      const payload = data as { url?: string; error?: string } | null;
      if (!payload?.url) return { error: payload?.error ?? "No session URL" };
      void reload();
      return { url: payload.url };
    },
    [reload],
  );

  return { view, start, reload };
}
